import React from 'react';
import styled from 'styled-components';
import { FiChevronLeft, FiChevronRight } from 'react-icons/fi';
import {
  colors,
  typography,
  spacing,
  borderRadius,
  shadows,
} from '../styles/designTokens';

// 모바일 페이지네이션 Props 인터페이스
interface PaginationMobileProps {
  currentPage: number;
  totalPages: number;
  total: number;
  limit: number;
  onPageChange: (page: number) => void;
}

const MobilePaginationContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing[3]};
  background-color: ${colors.semantic.background.primary};
  border-radius: ${borderRadius.xl};
  padding: ${spacing[4]};
  box-shadow: ${shadows.sm};
  border: 1px solid ${colors.semantic.border.primary};
`;

const MobilePageInfoContainer = styled.div`
  text-align: center;
  margin-bottom: ${spacing[2]};
`;

const MobilePageInfo = styled.div`
  font-size: ${typography.fontSize.base};
  font-weight: ${typography.fontWeight.semibold};
  color: ${colors.semantic.text.secondary};
  margin-bottom: ${spacing[1]};
`;

const MobileItemInfo = styled.div`
  font-size: 13px;
  color: ${colors.semantic.text.disabled};
`;

const MobilePageButtons = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: ${spacing[2]};
`;

const MobilePageButton = styled.button<{ isActive?: boolean; disabled?: boolean }>`
  min-width: 36px;
  height: 36px;
  border: 1px solid
    ${props => (props.isActive ? colors.primary[500] : colors.neutral[300])};
  background-color: ${props =>
    props.isActive ? colors.primary[500] : colors.semantic.background.primary};
  color: ${props =>
    props.isActive
      ? colors.semantic.text.inverse
      : colors.semantic.text.secondary};
  border-radius: ${borderRadius.md};
  font-size: ${typography.fontSize.sm};
  font-weight: ${typography.fontWeight.medium};
  cursor: ${props => (props.disabled ? 'not-allowed' : 'pointer')};
  transition: all 0.2s;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover:not(:disabled) {
    background-color: ${props =>
      props.isActive ? colors.primary[600] : colors.neutral[100]};
    border-color: ${props =>
      props.isActive ? colors.primary[600] : colors.secondary[400]};
  }

  &:active:not(:disabled) {
    transform: scale(0.95);
  }

  &:disabled {
    opacity: 0.5;
    background-color: ${colors.neutral[50]};
    color: ${colors.semantic.text.disabled};
    border-color: ${colors.neutral[200]};
  }
`;

const MobileNavButton = styled(MobilePageButton)`
  padding: 0 ${spacing[3]};

  .icon {
    font-size: ${typography.fontSize.base};
  }
`;

const MobilePageInputArea = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: ${spacing[2]};
  margin-top: ${spacing[2]};
`;

const MobilePageInputLabel = styled.span`
  font-size: ${typography.fontSize.sm};
  color: ${colors.semantic.text.secondary};
`;

const MobilePageInput = styled.input`
  width: 60px;
  padding: ${spacing[2]};
  border: 1px solid ${colors.neutral[300]};
  border-radius: ${borderRadius.md};
  font-size: ${typography.fontSize.sm};
  text-align: center;
  font-family: ${typography.fontFamily.sans};
  color: ${colors.semantic.text.primary};

  &:focus {
    outline: none;
    border-color: ${colors.primary[500]};
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
  }
`;

const MobileGoButton = styled.button`
  padding: ${spacing[2]} ${spacing[3]};
  background-color: ${colors.primary[500]};
  color: ${colors.semantic.text.inverse};
  border: none;
  border-radius: ${borderRadius.md};
  font-size: ${typography.fontSize.sm};
  font-weight: ${typography.fontWeight.medium};
  cursor: pointer;
  transition: background-color 0.2s;

  &:hover {
    background-color: ${colors.primary[600]};
  }

  &:active {
    transform: scale(0.95);
  }
`;

// 모바일 페이지네이션 컴포넌트
const PaginationMobile: React.FC<PaginationMobileProps> = ({
  currentPage,
  totalPages,
  total,
  limit,
  onPageChange
}) => {
  const [inputPage, setInputPage] = React.useState<string>('');

  // 페이지 범위 계산 - < 1,2,3,4,5 > 형태 (모바일)
  const getVisiblePages = () => {
    const maxVisible = 5; // 최대 5개 페이지 표시
    const pages = [];

    if (totalPages <= maxVisible) {
      // 총 페이지가 5개 이하면 모두 표시
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      // 현재 페이지를 중심으로 5개 페이지 표시
      let start = Math.max(1, currentPage - 2);
      let end = Math.min(totalPages, start + maxVisible - 1);

      // 끝에서부터 계산해서 시작점 조정
      if (end - start + 1 < maxVisible) {
        start = Math.max(1, end - maxVisible + 1);
      }

      for (let i = start; i <= end; i++) {
        pages.push(i);
      }
    }

    return pages;
  };

  // 직접 페이지 이동 핸들러
  const handleGoToPage = () => {
    const pageNum = parseInt(inputPage);
    if (pageNum >= 1 && pageNum <= totalPages) {
      onPageChange(pageNum);
      setInputPage('');
    }
  };

  // 엔터 키 핸들러
  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleGoToPage();
    }
  };

  const visiblePages = getVisiblePages();
  const startItem = (currentPage - 1) * limit + 1;
  const endItem = Math.min(currentPage * limit, total);

  return (
    <MobilePaginationContainer>
      {/* 페이지 정보 */}
      <MobilePageInfoContainer>
        <MobilePageInfo>
          {currentPage} / {totalPages} 페이지
        </MobilePageInfo>
        <MobileItemInfo>
          {total > 0 ? `${startItem}-${endItem} / 총 ${total}개` : '검색 결과가 없습니다'}
        </MobileItemInfo>
      </MobilePageInfoContainer>

      {/* 페이지 버튼들 - 1페이지라도 표시 */}
      <MobilePageButtons>
        {/* 이전 페이지 버튼 */}
        <MobileNavButton
          disabled={currentPage === 1}
          onClick={() => onPageChange(currentPage - 1)}
        >
          <FiChevronLeft className="icon" />
        </MobileNavButton>

        {/* 페이지 번호 버튼들 */}
        {visiblePages.map((page) => (
          <MobilePageButton
            key={page}
            isActive={page === currentPage}
            onClick={() => onPageChange(page)}
          >
            {page}
          </MobilePageButton>
        ))}

        {/* 다음 페이지 버튼 */}
        <MobileNavButton
          disabled={currentPage === totalPages}
          onClick={() => onPageChange(currentPage + 1)}
        >
          <FiChevronRight className="icon" />
        </MobileNavButton>
      </MobilePageButtons>

      {/* 직접 페이지 이동 */}
      {totalPages > 5 && (
        <MobilePageInputArea>
          <MobilePageInputLabel>페이지:</MobilePageInputLabel>
          <MobilePageInput
            type="number"
            min="1"
            max={totalPages}
            value={inputPage}
            onChange={(e) => setInputPage(e.target.value)}
            onKeyPress={handleKeyPress}
            placeholder={currentPage.toString()}
          />
          <MobilePageInputLabel>/ {totalPages}</MobilePageInputLabel>
          <MobileGoButton onClick={handleGoToPage}>
            이동
          </MobileGoButton>
        </MobilePageInputArea>
      )}
    </MobilePaginationContainer>
  );
};

export default PaginationMobile; 